import React, { FC, useContext, useState } from 'react'
import styled from 'styled-components'

import BagIcon from '@Assets/SVG/Bag'
import HeartIcon from '@Assets/SVG/Heart'
import SearchIcon from '@Assets/SVG/Search'
import UserIcon from '@Assets/SVG/User'
import { Abundance } from '@Components/Badges/Abundance'
import { BurgerMenuIcon, IconWithLabel, IconWithLabelProps, SVGIcon } from '@Components/Icons'
import { Link } from '@Components/Link'
import { LoginModals } from '@Components/Login'
import { MobileMenu } from '@Components/MobileMenu'
import { useWishList } from '@Components/WishList/useWishList'
import { handleEvent } from '@Events/Manager'
import { NavigationEvent } from '@Events/Navigation'
import { OtherEvent } from '@Events/Other'
import { MetadataContext } from '@Globals/Metadata'
import { SearchContext } from '@Globals/Search/Context'
import { up } from '@Globals/Style/breakpoints'
import { resetButton } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'
import { UserContext } from '@Globals/User'

type State = {
  isMenuOpen: boolean
  isSubMenuOpen: boolean
  selectedCategoryID?: string
  useDelayOnClose: boolean
}

const defaultState: State = {
  isMenuOpen: false,
  isSubMenuOpen: false,
  selectedCategoryID: undefined,
  useDelayOnClose: false,
}

interface IconBarWrapperProps {
  bottomBorderColor?: string
}

const iconSmallTextBelowPhablet = (
  storeID: string,
): IconWithLabelProps['smallTextBelowPhablet'] => {
  switch (storeID) {
    case '15':
    case '24':
      return true
    default:
      return false
  }
}

const iconSmallTextBelowPhone = (
  storeID: string,
): IconWithLabelProps['smallTextBelowPhablet'] => {
  switch (storeID) {
    case '6':
    case '9':
      return true
    default:
      return false
  }
}

const IconBarWrapper = styled.div<IconBarWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* TODO: include in theme/color system as greyLightest darkened 5% */
  border-bottom: ${({ bottomBorderColor }) => `1px solid ${bottomBorderColor ?? '#e8e8e8'}`};
  padding-bottom: 2px;

  > * {
    user-select: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  ${up('desktop')} {
    display: none;
  }

  .icon {
    ${resetButton};
    width: calc(20% + 10px);
    height: 53px;
    padding: 8px 0;
    transform: translateY(4px);

    &:first-child, &:last-child {
      width: calc(20% - 10px);
    }
  }
`

export const IconBar: FC = () => {
  const { t } = useTranslation()
  const { store, storeURL } = useContext(MetadataContext)
  const { itemsInWishlist } = useWishList()
  const { userData } = useContext(UserContext)
  const { setActive: setSearchActive } = useContext(SearchContext)

  const accountURL = storeURL('customer/account')
  const cartURL = storeURL('checkout/cart/')
  const wishListURL = storeURL('account/wishlist/')

  const [loginModalOpen, setLoginModalOpen] = useState(false)

  const [
    {
      isMenuOpen,
      isSubMenuOpen,
      selectedCategoryID,
      useDelayOnClose,
    },
    setState,
  ] = useState(defaultState)

  const openMobileMenu = () => {
    setState(prev => ({
      ...prev,
      isMenuOpen: !prev.isMenuOpen,
    }))
    handleEvent(new OtherEvent({
      attributes: {
        is_opened: true,
      },
      eventName: 'nav_mobile_menu_toggle',
    }))
  }

  const closeMobileMenu = () => {
    setState(prev => ({
      ...prev,
      isMenuOpen: false,
      isSubMenuOpen: false,
      useDelayOnClose: prev.isSubMenuOpen,
    }))
  }

  const onTopCategoryClick = (categoryID: string) => {
    setState(prev => ({
      ...prev,
      isSubMenuOpen: true,
      selectedCategoryID: categoryID,
    }))
    handleEvent(new OtherEvent({
      attributes: {
        category_id: categoryID,
        is_opened: true,
      },
      eventName: 'nav_mobile_menu_category_toggle',
    }))
  }

  const onCloseSubCategory = () => {
    setState(prev => ({
      ...prev,
      isSubMenuOpen: false,
    }))
  }

  return (
    <>
      <IconBarWrapper>
        {/* Menu */}
        <button className="icon" onClick={openMobileMenu} type="button">
          <IconWithLabel
            label={t('header:HEADER_MOBILE_MENU_LABEL')}
            smallTextBelowPhablet={iconSmallTextBelowPhablet(store?.id)}
            smallTextBelowPhone={iconSmallTextBelowPhone(store?.id)}
          >
            <BurgerMenuIcon />
          </IconWithLabel>
        </button>

        {/* Search */}
        <button className="icon" onClick={() => setSearchActive(true)} type="button">
          <IconWithLabel
            label={t('header:HEADER_MOBILE_SEARCH_LABEL')}
            smallTextBelowPhablet={iconSmallTextBelowPhablet(store?.id)}
            smallTextBelowPhone={iconSmallTextBelowPhone(store?.id)}
          >
            <SVGIcon icon={SearchIcon} />
          </IconWithLabel>
        </button>

        {/* User account */}
        {userData?.loggedIn && (
          <Link
            className="icon"
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'customer-area',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={accountURL}
          >
            <IconWithLabel
              label={t('header:MY_ACCOUNT_LABEL')}
              smallTextBelowPhablet={iconSmallTextBelowPhablet(store?.id)}
              smallTextBelowPhone={iconSmallTextBelowPhone(store?.id)}
            >
              <SVGIcon icon={UserIcon} />
            </IconWithLabel>
          </Link>
        )}
        {!userData?.loggedIn && (
          <button
            className="icon"
            onClick={() => setLoginModalOpen(true)}
            type="button"
          >
            <IconWithLabel
              label={t('header:LOG_IN_LABEL')}
              smallTextBelowPhablet={iconSmallTextBelowPhablet(store?.id)}
              smallTextBelowPhone={iconSmallTextBelowPhone(store?.id)}
            >
              <SVGIcon icon={UserIcon} />
            </IconWithLabel>
          </button>
        )}

        {/* Wish list */}
        <Link
          className="icon"
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'wish-list',
              dest_page_type_id: '',
              source_id: 'header',
            },
            eventName: 'nav_link_click',
          })}
          href={wishListURL}
        >
          <IconWithLabel
            label={t('header:WISH_LIST_LABEL')}
            smallTextBelowPhablet={iconSmallTextBelowPhablet(store?.id)}
            smallTextBelowPhone={iconSmallTextBelowPhone(store?.id)}
          >
            <Abundance count={itemsInWishlist}>
              <SVGIcon icon={HeartIcon} />
            </Abundance>
          </IconWithLabel>
        </Link>

        {/* Cart */}
        <Link
          className="icon"
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'cart',
              dest_page_type_id: '',
              source_id: 'header',
            },
            eventName: 'nav_link_click',
          })}
          href={cartURL}
        >
          <IconWithLabel
            label={t('header:CART_SHORT_LABEL')}
            smallTextBelowPhablet={iconSmallTextBelowPhablet(store?.id)}
            smallTextBelowPhone={iconSmallTextBelowPhone(store?.id)}
          >
            <Abundance count={userData?.basket?.quantity}>
              <SVGIcon icon={BagIcon} />
            </Abundance>
          </IconWithLabel>
        </Link>
      </IconBarWrapper>

      {/* Mobile menu */}
      <MobileMenu
        closeMobileMenu={closeMobileMenu}
        isMenuOpen={isMenuOpen}
        isSubMenuOpen={isSubMenuOpen}
        onCloseSubCategory={onCloseSubCategory}
        onTopCategoryClick={onTopCategoryClick}
        selectedCategoryID={selectedCategoryID}
        useDelayOnClose={useDelayOnClose}
      />

      <LoginModals
        isOpen={loginModalOpen}
        onHide={() => setLoginModalOpen(false)}
        sourceID="header"
      />
    </>
  )
}
